import deburr from 'lodash/deburr';
import kebabCase from 'lodash/kebabCase';
import orderBy from 'lodash/orderBy';

import { __t, applyTranslations } from 'utils/language';

import type { CheckboxesListItem } from 'types';
import type {
  SOVGeneralItem,
  SOVGeneralModalTextFields,
  SOVQueryCommon,
  SOVQueryEndpoint,
  SOVQueryEndpointMulti,
  SOVQueryEndpointMultiGroup,
  SOVQueryIds,
  SOVQueryLocal,
  SOVQueryMergedKeywordsMulti,
  SOVQuerySearchResultsAll,
  SOVQuerySearchResultsCheckboxes,
  SOVQueryStep
} from 'modules/insights/sov/types';

applyTranslations(require('modules/insights/sov/components/queries/modals/sov-queries-modal.i18n.json'));

const MODAL_MSG_KEY = 'sov_queries_modal';

export const MIN_CHAR_COUNT_FOR_QUERY = 2;

export const PRIMARY = 'primary';
export const SECONDARY = 'secondary';
export const SINGLE = 'single';
export const MULTI = 'multi';
export const ALL = 'all';
export const HASHTAGS = 'hashtags';
export const MENTIONS = 'mentions';
export const TAGS = 'tags';
export const CHANNELS = 'channels';
export const HASHTAG = 'hashtag';
export const MENTION = 'mention';
export const TAG = 'tag';
export const CHANNEL = 'channel';
export const TITLE = 'title';
export const DESCRIPTION = 'description';

export const TAB_LABELS = [HASHTAGS, MENTIONS, TAGS];

export const STEP_SEARCH_TERMS = 'search-terms';
export const STEP_SEARCH_RESULTS = 'search-results';
export const STEP_RELATED_CHANNELS = 'related-channels';

export const STEPS_SEARCH_TERMS: Array<SOVQueryStep> = [{
  key: STEP_SEARCH_TERMS,
  index: 1,
  queryType: ALL,
  stepType: SINGLE
}];

export const STEPS_RESULTS: Array<SOVQueryStep> = [
  {
    key: STEP_SEARCH_RESULTS,
    index: 2,
    queryType: ALL,
    stepType: PRIMARY,
    isResultsStep: true
  },
  {
    key: STEP_RELATED_CHANNELS,
    index: 3,
    queryType: ALL,
    stepType: PRIMARY,
    isResultsStep: true
  },
  {
    key: STEP_SEARCH_RESULTS,
    index: 4,
    queryType: MULTI,
    stepType: SECONDARY,
    isResultsStep: true
  },
  {
    key: STEP_RELATED_CHANNELS,
    index: 5,
    queryType: MULTI,
    stepType: SECONDARY,
    isResultsStep: true
  }
];

export const STEPS = [
  ...STEPS_SEARCH_TERMS,
  ...STEPS_RESULTS
];

const SYMBOL_MAP = {
  hashtag: '#',
  mention: '@',
  tag: '@',
  channel: '@'
};

const KEYWORD_TYPES = [
  'includedKeywords',
  'customKeywords',
  'channels'
];

export const QUERY_COMMON = {
  channels: [],
  customKeywords: [],
  includedKeywords: [],
  name: ''
};

export const getSingular = (value: string): string => value.substring(0, value.length - 1);

export const getSymbol = (type?: string | null): string => SYMBOL_MAP[type || HASHTAG];

export const removeExtraLabelInfo = (label?: string | null): string => (label || '').replace(/#|@|\(.*\)/g, '').trim();

export const getCountString = (count: number | undefined | null, type: string): string => {
  const countString = type === CHANNEL
    ? ` ${__t(`${MODAL_MSG_KEY}.labels.matching_posts`)}`
    : '';

  return count ? ` (${count}${countString})` : '';
};

export const convertCheckboxToItem = (item: CheckboxesListItem): SOVGeneralItem => ({
  keyword: removeExtraLabelInfo((typeof item.label === 'string' ? item.label : null)),
  selected: item.isSelected,
  type: item.type
});

export const convertItemToCheckbox = (item: SOVGeneralItem, index: number): CheckboxesListItem => {
  const type = item.type || HASHTAG;
  const countString = getCountString(item.count, type);
  const symbol = getSymbol(type) || '';

  return {
    id: `${type}-${kebabCase(deburr(item.keyword))}-${index}`,
    label: `${symbol}${item.keyword}${countString}`,
    type,
    isSelected: item.selected
  };
};

export const getActiveQuery = (queryId: number, mergedQueries: Array<SOVQueryLocal>): Partial<SOVQueryLocal> => {
  const foundItem = mergedQueries.find(item => {
    if (item.type === SINGLE && queryId === item.id) {
      return item;
    }

    if (item.type === MULTI && (queryId === item.ids?.primary || queryId === item.ids?.secondary)) {
      return item;
    }

    return undefined;
  });

  return foundItem || {};
};

export const convertMergedQuerySingleToEndpoint = (mergedQuery: SOVQueryLocal): SOVQueryEndpoint => ({
  enabled: mergedQuery.enabled,
  type: SINGLE,
  reportId: mergedQuery.reportId,
  id: mergedQuery.id,
  name: mergedQuery.name,
  channels: mergedQuery.channels.single.general,
  customKeywords: mergedQuery.customKeywords.single.general,
  includedKeywords: mergedQuery.includedKeywords.single.general
});

export const convertMergedQueryMultiToEndpoint = (mergedQuery: SOVQueryLocal): Partial<SOVQueryEndpoint> => {
  const keywords: Record<string, Record<'primary' | 'secondary', Array<SOVGeneralItem>>> = {};

  KEYWORD_TYPES.forEach(type => {
    const primary = mergedQuery[type].primary;
    const secondary = mergedQuery[type].secondary;

    keywords[type] = {
      [PRIMARY]: primary.general,
      [SECONDARY]: secondary.general
    };
  });

  return {
    enabled: mergedQuery.enabled,
    reportId: mergedQuery.reportId,
    id: mergedQuery.ids.parent,
    type: MULTI,
    name: `${mergedQuery.names[PRIMARY]} + ${mergedQuery.names[SECONDARY]}`,
    queries: [
      {
        channels: keywords.channels[PRIMARY],
        customKeywords: keywords.customKeywords[PRIMARY],
        includedKeywords: keywords.includedKeywords[PRIMARY],
        reportId: mergedQuery.reportId,
        id: mergedQuery.ids[PRIMARY],
        name: mergedQuery.names[PRIMARY]
      },
      {
        channels: keywords.channels[SECONDARY],
        customKeywords: keywords.customKeywords[SECONDARY],
        includedKeywords: keywords.includedKeywords[SECONDARY],
        reportId: mergedQuery.reportId,
        id: mergedQuery.ids[SECONDARY],
        name: mergedQuery.names[SECONDARY]
      }
    ]
  };
};

export const convertLocalQueryToEndpoint = (mergedQuery: SOVQueryLocal): Partial<SOVQueryEndpoint> => {
  if (mergedQuery.type === MULTI) {
    return convertMergedQueryMultiToEndpoint(mergedQuery);
  }

  return convertMergedQuerySingleToEndpoint(mergedQuery);
};

export const getMergedKeywordsMulti = (primary: SOVQueryEndpoint, secondary: SOVQueryEndpoint): SOVQueryMergedKeywordsMulti => {
  const results = {} as SOVQueryMergedKeywordsMulti;

  KEYWORD_TYPES.forEach(type => {
    results[type] = {
      primary: {
        general: primary[type],
        checkbox: primary[type].map(convertItemToCheckbox)
      },
      secondary: {
        general: secondary[type],
        checkbox: secondary[type].map(convertItemToCheckbox)
      }
    };
  });

  return results;
};

// @ts-ignore
export const mergeQueriesSingle = (queriesSingle: Array<SOVQueryEndpoint>): Array<Partial<SOVQueryLocal>> => queriesSingle.map(item => ({
  ...item,
  type: SINGLE,
  includedKeywords: {
    single: {
      general: item.includedKeywords,
      checkbox: (item.includedKeywords || []).map(convertItemToCheckbox)
    }
  },
  customKeywords: {
    single: {
      general: item.customKeywords,
      checkbox: (item.customKeywords || []).map(convertItemToCheckbox)
    }
  },
  channels: {
    single: {
      general: item.channels,
      checkbox: (item.channels || []).map(convertItemToCheckbox)
    }
  }
}));

export const mergeQueriesMulti = (queriesMulti: Array<SOVQueryEndpointMulti>): Array<Partial<SOVQueryLocal>> => queriesMulti
  .map(queryMulti => ({
    enabled: queryMulti.enabled,
    parentId: queryMulti.id,
    queries: queryMulti.queries
  }))
  .reduce((acc, item) => {
    const queries = item.queries;
    const mergedKeywords = getMergedKeywordsMulti(queries[0], queries[1]);

    return acc.concat([{
      ...mergedKeywords,
      enabled: item.enabled,
      type: MULTI,
      reportId: queries[0].reportId,
      id: queries[0].id,
      ids: {
        primary: queries[0].id,
        secondary: queries[1].id,
        parent: item.parentId
      },
      name: `${queries[0].name} + ${queries[1].name}`,
      names: {
        primary: queries[0].name,
        secondary: queries[1].name,
        full: `${queries[0].name} + ${queries[1].name}`
      }
    } as Partial<SOVQueryLocal>]);
  }, [] as Array<Partial<SOVQueryLocal>>);

export const mergeQueries = (
  queriesSingle: Array<SOVQueryEndpoint>,
  queriesMulti: Array<SOVQueryEndpointMulti>
): Array<Partial<SOVQueryLocal>> => {
  const preparedQueriesSingle = mergeQueriesSingle(queriesSingle);
  const preparedQueriesMulti = mergeQueriesMulti(queriesMulti);

  return orderBy(preparedQueriesSingle.concat(preparedQueriesMulti), 'id', 'desc');
};

export const getNonChannelData = (data: SOVQuerySearchResultsCheckboxes): Array<SOVGeneralItem> => {
  let updatedData = [];

  TAB_LABELS.forEach(tab => {
    const selecteditems = data[tab]
      .filter(item => item.isSelected)
      .map(convertCheckboxToItem);

    updatedData = updatedData.concat(selecteditems);
  });

  return updatedData;
};

export const getChannelData = (data: Array<CheckboxesListItem>): Array<SOVGeneralItem> => data.filter(item => item.isSelected)
  .map(convertCheckboxToItem);

export const prepareQueryResultsSingleForEndpoint = (results: SOVQuerySearchResultsAll, queryId?: number | null): Partial<SOVQueryCommon> => ({
  ...(queryId ? { id: queryId } : null),
  channels: getChannelData(results.channels[SINGLE]),
  customKeywords: getNonChannelData(results.custom[SINGLE]),
  includedKeywords: getNonChannelData(results.included[SINGLE])
});

export const prepareQueryResultsMultiForEndpoint = (results: SOVQuerySearchResultsAll, ids: SOVQueryIds): SOVQueryEndpointMultiGroup => ({
  [PRIMARY]: {
    id: ids?.primary,
    channels: getChannelData(results.channels[PRIMARY]),
    customKeywords: getNonChannelData(results.custom[PRIMARY]),
    includedKeywords: getNonChannelData(results.included[PRIMARY])
  },

  [SECONDARY]: {
    id: ids?.secondary,
    channels: getChannelData(results.channels[SECONDARY]),
    customKeywords: getNonChannelData(results.custom[SECONDARY]),
    includedKeywords: getNonChannelData(results.included[SECONDARY])
  }
});

export const getEndpointDataSingle = (
  preparedDataSingle: Partial<SOVQueryCommon>,
  reportId: number,
  textFields: SOVGeneralModalTextFields
): Partial<SOVQueryEndpoint> => ({
  name: textFields[SINGLE],
  reportId,
  id: preparedDataSingle.id,
  channels: preparedDataSingle.channels,
  customKeywords: preparedDataSingle.customKeywords,
  includedKeywords: preparedDataSingle.includedKeywords
});

export const getEndpointDataMulti = (
  preparedDataMulti: SOVQueryEndpointMultiGroup,
  ids: SOVQueryIds,
  reportId: number,
  textFields: SOVGeneralModalTextFields
): Partial<SOVQueryEndpoint> => ({
  name: `${textFields[PRIMARY]} + ${textFields[SECONDARY]}`,
  reportId,
  id: ids?.parent,

  queries: [
    {
      reportId,
      id: ids?.primary,
      channels: preparedDataMulti[PRIMARY]?.channels,
      customKeywords: preparedDataMulti[PRIMARY]?.customKeywords,
      includedKeywords: preparedDataMulti[PRIMARY]?.includedKeywords,
      name: textFields[PRIMARY]
    },
    {
      reportId,
      id: ids?.secondary,
      channels: preparedDataMulti[SECONDARY]?.channels,
      customKeywords: preparedDataMulti[SECONDARY]?.customKeywords,
      includedKeywords: preparedDataMulti[SECONDARY]?.includedKeywords,
      name: textFields[SECONDARY]
    }
  ]
});

export const getEndpointData = (
  preparedDataSingle: Partial<SOVQueryCommon>,
  preparedDataMulti: SOVQueryEndpointMultiGroup,
  reportId: number,
  textFields: SOVGeneralModalTextFields,
  ids: SOVQueryIds,
  isMulti: boolean
): Partial<SOVQueryEndpoint> => {
  const endpointDataSingle = !isMulti ? getEndpointDataSingle(preparedDataSingle, reportId, textFields) : {};
  const endpointDataMulti = isMulti ? getEndpointDataMulti(preparedDataMulti, ids, reportId, textFields) : {};

  if (isMulti) {
    return endpointDataMulti;
  }

  return endpointDataSingle;
};
